import React, {
    useCallback,
    useState,
    useRef,
    useEffect,
    ChangeEvent,
    KeyboardEvent,
    useContext
} from 'react';
import { AiOutlineFileAdd } from 'react-icons/ai';
import { MdClose, MdVideoLibrary } from 'react-icons/md';
import { RiFileTextFill } from 'react-icons/ri';
import {
    submitEditAssistant,
    deleteAssistantFiles,
    enableAssistant,
    resolveEntities
} from '../../../api/api';
import { Assistant, AssistantFile, AzureGroup, SearchEntity, UploadedFile } from '../../../api/models';
import { AppStateContext } from '../../../state/AppProvider';
import ClipLoader from "react-spinners/ClipLoader";
import { ErrorDialog } from '../../ErrorDialog';
import { useFileProcessing } from '../../../hooks/useFileProcessing';
import { FileProcessingModal } from '../ui/FileProcessingModal';
import { TitleField } from '../ui/TitleField';
import { UrlField } from '../ui/UrlField';
import { PromptField } from '../ui/PromptField';
import { WelcomeField } from '../ui/WelcomeField';
import { ToolsField } from '../ui/ToolsField';
import { UsersField } from '../ui/UsersField';
import { useAssistantsRefresh } from '../hooks/useAssistantsRefresh';
import { DOCUMENT_TYPES_LABEL, KNOWLEDGE_ACCEPT, getKnowledgeFileType, knowledgeRejectionMessage } from '../../../constants/fileUpload';

import '../ui/AssistantBuilder.css';

interface AssistantEditorProps {
    id: string;
    onSubmit: () => void;
    onCancel: () => void;
}

export const AssistantEditor = ({
    id,
    onSubmit,
    onCancel,
}: AssistantEditorProps) => {
    const appStateContext = useContext(AppStateContext);

    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [assistantFiles, setAssistantFiles] = useState<AssistantFile[]>([]);
    const [deletedFiles, setDeletedFiles] = useState<AssistantFile[]>([]);
    const [title, setTitle] = useState('');
    const [oldTitle, setOldTitle] = useState('');
    const [promptMessage, setPromptMessage] = useState('');
    const [oldPromptMessage, setOldPromptMessage] = useState('');
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [oldWelcomeMessage, setOldWelcomeMessage] = useState('');
    const [assistantUrl, setAssistantUrl] = useState('');
    const [oldAssistantUrl, setOldAssistantUrl] = useState('');
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentPhase, setCurrentPhase] = useState<string>('');
    const [selectedEntities, setSelectedEntities] = useState<SearchEntity[]>([]);
    const [existingEntities, setExistingEntities] = useState<SearchEntity[]>([]);
    const [selectedManagerEntities, setSelectedManagerEntities] = useState<SearchEntity[]>([]);
    const [existingManagerEntities, setExistingManagerEntities] = useState<SearchEntity[]>([]);
    const [enabledTools, setEnabledTools] = useState<Set<string>>(new Set());
    const [oldTools, setOldTools] = useState<Set<string>>(new Set());
    const [toolDefaults, setToolDefaults] = useState<Set<string>>(new Set());
    const [oldToolDefaults, setOldToolDefaults] = useState<Set<string>>(new Set());
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const file_limit = 500;
    const document_size_limit = 20;
    const video_size_limit = 2048;
    const document_size_limit_bytes = document_size_limit * 1024 * 1024;
    const video_size_limit_bytes = video_size_limit * 1024 * 1024;
    const video_size_limit_gb = video_size_limit / 1024;

    const {
        currentFileProgress,
        currentFileIndex,
        totalFiles,
        uploadDocument,
        uploadVideo,
        resetProgress
    } = useFileProcessing();

    const openErrorDialog = (msg: string) => {
        setErrorMessage(msg);
        setShowErrorDialog(true);
    };

    const toggleTool = (tool: string) => {
        if (enabledTools.has(tool)) {
            setToolDefaults(prev => {
                const next = new Set(prev);
                next.delete(tool);
                return next;
            });
        }
        setEnabledTools(prev => {
            const next = new Set(prev);
            if (next.has(tool)) next.delete(tool);
            else next.add(tool);
            return next;
        });
    };

    const toggleDefaultTool = (tool: string) => {
        if (!enabledTools.has(tool)) return;
        setToolDefaults(prev => {
            const next = new Set(prev);
            if (next.has(tool)) next.delete(tool);
            else next.add(tool);
            return next;
        });
    };

    const builderManagerGroupId = appStateContext?.state.assistantBuilderManagerGroupId ?? null;

    useEffect(() => {
        const fetchAssistantDetails = async () => {
            const all = appStateContext?.state.assistants || []
            const a = all.find(x => x.id === id)
            if (!a) return

            setTitle(a.title)
            setOldTitle(a.title)
            setPromptMessage(a.system_prompt_message)
            setOldPromptMessage(a.system_prompt_message)
            setWelcomeMessage(a.welcome_message)
            setOldWelcomeMessage(a.welcome_message)
            setAssistantFiles(a.files || [])
            setAssistantUrl((a.url || '').toLowerCase())
            setOldAssistantUrl((a.url || '').toLowerCase())

            const toolsSet = new Set<string>(a.tools || []);
            setEnabledTools(toolsSet);
            setOldTools(toolsSet);

            const defaultsSet = new Set<string>(a.tool_defaults || []);
            setToolDefaults(defaultsSet);
            setOldToolDefaults(defaultsSet);

            const uids = a.userIds ?? []
            const gids = a.groups ?? []
            if (uids.length > 0 || gids.length > 0) {
                try {
                    const ents = await resolveEntities(uids, gids)
                    setExistingEntities(ents)
                    setSelectedEntities(ents)
                } catch (err) {
                    console.error("resolveEntities failed:", err)
                }
            }

            // The assistant builder manager group is always a manager and cannot
            // be removed; merge it in for legacy assistants that predate it.
            const managerUids = a.managerIds ?? []
            const managerGids = [...(a.managerGroups ?? [])]
            if (builderManagerGroupId && !managerGids.includes(builderManagerGroupId)) {
                managerGids.push(builderManagerGroupId)
            }
            let managerEnts: SearchEntity[] = []
            if (managerUids.length > 0 || managerGids.length > 0) {
                try {
                    managerEnts = await resolveEntities(managerUids, managerGids)
                } catch (err) {
                    console.error("resolveEntities failed:", err)
                }
            }
            if (builderManagerGroupId && !managerEnts.some(e => e.id === builderManagerGroupId)) {
                const fallback: AzureGroup = {
                    id: builderManagerGroupId,
                    displayName: 'Assistant Builder Managers',
                    objectType: 'group'
                }
                managerEnts = [fallback, ...managerEnts]
            }
            setExistingManagerEntities(managerEnts)
            setSelectedManagerEntities(managerEnts)
        }

        fetchAssistantDetails()
    }, [id, builderManagerGroupId])

    const handleEntitySelect = (entity: SearchEntity) => {
        setSelectedEntities((prevEntities) => [...prevEntities, entity]);
    };

    const handleEntityRemove = (entityId: string) => {
        setSelectedEntities((prevEntities) => prevEntities.filter((entity) => entity.id !== entityId));
    };

    const handleManagerSelect = (entity: SearchEntity) => {
        setSelectedManagerEntities((prevEntities) => [...prevEntities, entity]);
    };

    const handleManagerRemove = (entityId: string) => {
        setSelectedManagerEntities((prevEntities) => prevEntities.filter((entity) => entity.id !== entityId));
    };

    const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value.slice(0, 25);
        setTitle(newValue);
    };

    const handleTitleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (title.length >= 25 && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
        }
    };

    const handlePromptMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value.slice(0, 10000);
        setPromptMessage(newValue);
    };

    const handlePromptMessageKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (promptMessage.length >= 10000 && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
        }
    };

    const handleWelcomeMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value.slice(0, 1500);
        setWelcomeMessage(newValue);
    };

    const handleWelcomeMessageKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (welcomeMessage.length >= 1500 && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
        }
    };

    const handleAssistantUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const sanitized = raw.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setAssistantUrl(sanitized);
    };

    const allAssistants = appStateContext?.state?.assistants || [];
    const existingAssistantUrls: string[] = allAssistants
        .filter((a: Assistant) => a?.id !== id)
        .map((a: Assistant) => (a?.url || '').toLowerCase())
        .filter(Boolean);

    const isAssistantUrlDuplicate = assistantUrl.length > 0 && existingAssistantUrls.includes(assistantUrl.toLowerCase());
    const fullUrlPreview = `https://chat.jtcgroup.ai/${assistantUrl}`;

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const addFiles = useCallback((files: File[]) => {
        setUploadedFiles((prevUploaded) => {
            const currentTotal = assistantFiles.length + prevUploaded.length;
            const validNewFiles: UploadedFile[] = [];

            files.forEach((file) => {
                const fileType = getKnowledgeFileType(file.name);

                if (!fileType) {
                    openErrorDialog(knowledgeRejectionMessage(file.name));
                    return;
                }

                const sizeLimit = fileType === 'document' ? document_size_limit_bytes : video_size_limit_bytes;
                const sizeLimitDisplay = fileType === 'document' ? `${document_size_limit}MB` : `${video_size_limit_gb}GB`;

                if (file.size > sizeLimit) {
                    openErrorDialog(`File "${file.name}" exceeds ${sizeLimitDisplay} file size limit`);
                    return;
                }

                const duplicateInAssistant = assistantFiles.some(
                    (existing) => existing.fileName === file.name
                );
                const duplicateInUploaded = prevUploaded.some(
                    (uploaded) => uploaded.file.name === file.name
                );
                const duplicateInNew = validNewFiles.some(
                    (newFile) => newFile.file.name === file.name
                );

                if (duplicateInAssistant || duplicateInUploaded || duplicateInNew) {
                    return;
                }

                validNewFiles.push({ file, type: fileType });
            });

            const allowedFilesCount = file_limit - currentTotal;
            if (allowedFilesCount <= 0) {
                openErrorDialog(`You can only have up to ${file_limit} files.`);
                return prevUploaded;
            }

            if (validNewFiles.length > allowedFilesCount) {
                openErrorDialog(`The file limit is ${file_limit} file(s). Some files were not added.`);
                validNewFiles.splice(allowedFilesCount);
            }

            return [...prevUploaded, ...validNewFiles];
        });
    }, [assistantFiles, file_limit]);

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(false);
        const files = Array.from(event.dataTransfer.files);
        addFiles(files);
    }, [addFiles]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        addFiles(files);
    };

    const handleRemoveCombinedFile = (index: number) => {
        if (index < assistantFiles.length) {
            const fileToRemove = assistantFiles[index];
            setAssistantFiles((prev) => prev.filter((_, i) => i !== index));
            setDeletedFiles((prev) => [...prev, fileToRemove]);
        } else {
            const uploadedIndex = index - assistantFiles.length;
            setUploadedFiles((prev) => prev.filter((_, i) => i !== uploadedIndex));
        }
    };

    const formatFileSize = (size: number) => {
        if (size >= 1024 * 1024 * 1024) {
            return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
        } else if (size >= 1024 * 1024) {
            return (size / (1024 * 1024)).toFixed(2) + ' MB';
        } else if (size >= 1024) {
            return (size / 1024).toFixed(2) + ' KB';
        } else {
            return size + ' B';
        }
    };

    const refreshAssistantsList = useAssistantsRefresh({ updateActiveAssistant: true });

    const toolsChanged =
        enabledTools.size !== oldTools.size ||
        [...enabledTools].some(t => !oldTools.has(t));

    const toolDefaultsChanged =
        toolDefaults.size !== oldToolDefaults.size ||
        [...toolDefaults].some(t => !oldToolDefaults.has(t));

    const oldEntityIds = new Set(existingEntities.map(e => e.id));
    const entitiesChanged =
        selectedEntities.length !== existingEntities.length ||
        selectedEntities.some(e => !oldEntityIds.has(e.id));

    const oldManagerIds = new Set(existingManagerEntities.map(e => e.id));
    const managersChanged =
        selectedManagerEntities.length !== existingManagerEntities.length ||
        selectedManagerEntities.some(e => !oldManagerIds.has(e.id));

    const handleSubmit = async () => {
        if (
            title === oldTitle &&
            promptMessage === oldPromptMessage &&
            welcomeMessage === oldWelcomeMessage &&
            assistantUrl === oldAssistantUrl &&
            !entitiesChanged &&
            !managersChanged &&
            !toolsChanged &&
            !toolDefaultsChanged &&
            uploadedFiles.length === 0 &&
            deletedFiles.length === 0
        ) {
            onSubmit();
            return;
        }

        setIsSubmitting(true);
        setFormSubmitted(true);
        resetProgress();

        if (!title || !promptMessage || !assistantUrl) {
            openErrorDialog('Title, assistant URL, and prompt message are required.');
            setIsSubmitting(false);
            return;
        }

        if (isAssistantUrlDuplicate) {
            openErrorDialog('Assistant URL must be unique.');
            setIsSubmitting(false);
            return;
        }

        try {
            const metadataChanged =
                title !== oldTitle ||
                promptMessage !== oldPromptMessage ||
                welcomeMessage !== oldWelcomeMessage ||
                assistantUrl !== oldAssistantUrl ||
                entitiesChanged ||
                managersChanged ||
                toolsChanged ||
                toolDefaultsChanged;

            if (metadataChanged) {
                setCurrentPhase('Updating assistant details...');
                const selectedUserIds = selectedEntities
                    .filter((e) => e.objectType === 'user')
                    .map((e) => e.id);
                const selectedGroupIds = selectedEntities
                    .filter((e) => e.objectType === 'group')
                    .map((e) => e.id);
                const selectedManagerIds = selectedManagerEntities
                    .filter((e) => e.objectType === 'user')
                    .map((e) => e.id);
                const selectedManagerGroupIds = selectedManagerEntities
                    .filter((e) => e.objectType === 'group')
                    .map((e) => e.id);

                const editResponse = await submitEditAssistant(
                    id,
                    title,
                    assistantUrl,
                    promptMessage,
                    welcomeMessage,
                    selectedUserIds,
                    selectedGroupIds,
                    Array.from(enabledTools),
                    Array.from(toolDefaults),
                    selectedManagerIds,
                    selectedManagerGroupIds
                );
                if (!editResponse.ok) {
                    const errorText = await editResponse.text();
                    console.error('Error updating assistant:', errorText);
                    openErrorDialog('Failed to update assistant details.');
                    setIsSubmitting(false);
                    return;
                }
            }

            if (deletedFiles.length > 0) {
                setCurrentPhase(`Deleting ${deletedFiles.length} file${deletedFiles.length > 1 ? 's' : ''}...`);
                const deleteResponse = await deleteAssistantFiles(
                    id,
                    deletedFiles
                );
                if (!deleteResponse.ok) {
                    console.error('Error deleting files:', await deleteResponse.text());
                    openErrorDialog('Failed to delete some files.');
                    setIsSubmitting(false);
                    return;
                }
            }

            if (uploadedFiles.length > 0) {
                const documentFiles = uploadedFiles
                    .filter(uf => uf.type === 'document')
                    .map(uf => uf.file);
                const videoFiles = uploadedFiles
                    .filter(uf => uf.type === 'video')
                    .map(uf => uf.file);

                const allErrors: Array<{ filename: string; error: string }> = [];
                const totalFilesToProcess = documentFiles.length + videoFiles.length;

                for (let i = 0; i < documentFiles.length; i++) {
                    const documentFile = documentFiles[i];
                    setCurrentPhase(`Processing file ${i + 1} of ${totalFilesToProcess}: ${documentFile.name}`);

                    const result = await uploadDocument(
                        id,
                        documentFile,
                        i,
                        totalFilesToProcess
                    );

                    if (!result.success) {
                        allErrors.push({
                            filename: documentFile.name,
                            error: result.error || 'Unknown error'
                        });
                    }
                }

                for (let i = 0; i < videoFiles.length; i++) {
                    const videoFile = videoFiles[i];
                    const fileIndex = documentFiles.length + i;
                    setCurrentPhase(`Processing file ${fileIndex + 1} of ${totalFilesToProcess}: ${videoFile.name}`);

                    const result = await uploadVideo(
                        id,
                        videoFile,
                        fileIndex,
                        totalFilesToProcess
                    );

                    if (!result.success) {
                        allErrors.push({
                            filename: videoFile.name,
                            error: result.error || 'Unknown error'
                        });
                    }
                }

                if (allErrors.length > 0) {
                    const msg = allErrors.map(e => `${e.filename}: ${e.error}`).join('\n');
                    openErrorDialog(`Some files failed:\n${msg}`);
                    setIsSubmitting(false);
                    return;
                }
            }

            setCurrentPhase('Enabling assistant...');
            await enableAssistant(id);
            await refreshAssistantsList();
            setIsSuccess(true);
            setCurrentPhase('');
        } catch (error) {
            console.error('Error submitting the form:', error);
            openErrorDialog('An error occurred while submitting the form.');
            setIsSubmitting(false);
            setCurrentPhase('');
        } finally {
            if (isSuccess) {
                setIsSubmitting(false);
            }
        }
    };

    const combinedFiles = [
        ...assistantFiles.map(f => ({
            name: f.fileName,
            size: f.fileSize,
            type: getKnowledgeFileType(f.fileName) ?? 'document',
            isExisting: true
        })),
        ...uploadedFiles.map(uf => ({
            name: uf.file.name,
            size: uf.file.size,
            type: uf.type,
            isExisting: false
        }))
    ];

    const documentCount = combinedFiles.filter(uf => uf.type === 'document').length;
    const videoCount = combinedFiles.filter(uf => uf.type === 'video').length;

    return (
        <div className="relative flex flex-col h-full min-w-0">
            <FileProcessingModal
                isVisible={isSubmitting && !isSuccess}
                currentPhase={currentPhase}
                currentFileProgress={currentFileProgress}
                currentFileIndex={currentFileIndex}
                totalFiles={totalFiles}
                isEditing={true}
            />
            <fieldset className="h-full min-w-0 w-full">
                <div className="flex flex-col h-full relative min-w-0">
                    <div
                        className={
                            isSuccess
                                ? 'flex flex-grow items-center justify-center'
                                : 'flex-grow min-w-0 overflow-y-auto overflow-x-hidden p-4'
                        }
                    >
                        {isSuccess ? (
                            <div className="flex flex-col justify-center items-center">
                                <h2 className="text-xl font-semibold text-jtc-pink mb-2">
                                    Assistant Updated!
                                </h2>
                                <svg
                                    className="checkmark"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 52 52"
                                >
                                    <circle
                                        className="checkmark__circle"
                                        cx="26"
                                        cy="26"
                                        r="25"
                                        fill="none"
                                    />
                                    <path
                                        className="checkmark__check"
                                        fill="none"
                                        d="M14 27l7 7 16-16"
                                    />
                                </svg>
                            </div>
                        ) : (
                            <>
                                {/* Edit Title */}
                                <div className={`${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <TitleField
                                        heading="Edit Title"
                                        description={<><strong>Current Title:</strong> {oldTitle}</>}
                                        value={title}
                                        onChange={handleTitleChange}
                                        onKeyDown={handleTitleKeyDown}
                                        formSubmitted={formSubmitted}
                                        disabled={isSubmitting}
                                        baseInputClass="mt-1 block w-full rounded-md shadow-sm p-3 sm:text-sm border"
                                        counterClassName="text-sm text-gray-500 mt-1 text-right"
                                    />
                                </div>

                                {/* Edit Assistant URL */}
                                <div className={`mt-6 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <UrlField
                                        heading="Edit Assistant URL"
                                        description={<><strong>Current URL:</strong> {oldAssistantUrl || '(none)'}</>}
                                        value={assistantUrl}
                                        onChange={handleAssistantUrlChange}
                                        formSubmitted={formSubmitted}
                                        isDuplicate={isAssistantUrlDuplicate}
                                        disabled={isSubmitting}
                                        baseInputClass="mt-1 block w-full rounded-md shadow-sm p-3 sm:text-sm border"
                                        fullUrlPreview={fullUrlPreview}
                                    />
                                </div>

                                {/* Edit Prompt Message */}
                                <div className={`mt-6 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <PromptField
                                        heading="Edit Prompt Message"
                                        description={<><strong>Current Prompt Message:</strong> {oldPromptMessage}</>}
                                        value={promptMessage}
                                        onChange={handlePromptMessageChange}
                                        onKeyDown={handlePromptMessageKeyDown}
                                        formSubmitted={formSubmitted}
                                        disabled={isSubmitting}
                                        baseTextareaClass="mt-1 block w-full min-h-[150px] rounded-md shadow-sm p-3 sm:text-sm border"
                                        counterClassName="text-sm text-gray-500 mt-1 text-right"
                                    />
                                </div>

                                {/* Edit Welcome Message */}
                                <div className={`mt-6 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <WelcomeField
                                        heading="Edit Welcome Message"
                                        currentValueNode={
                                            <p className={`text-sm mb-4 break-words ${isSubmitting ? 'text-gray-400' : 'text-gray-500'}`}>
                                                <strong>Current Welcome Message:</strong> {oldWelcomeMessage || '(none)'}
                                            </p>
                                        }
                                        value={welcomeMessage}
                                        onChange={handleWelcomeMessageChange}
                                        onKeyDown={handleWelcomeMessageKeyDown}
                                        disabled={isSubmitting}
                                        textareaClassName="mt-1 block w-full min-h-[150px] rounded-md shadow-sm p-3 sm:text-sm border border-jtc-grey focus:border-jtc-blue focus:ring-1 focus:ring-jtc-blue focus:outline-none"
                                        counterClassName="text-sm text-gray-500 mt-1 text-right"
                                    />
                                </div>

                                {/* Edit Tools */}
                                <div className={`mt-6 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <ToolsField
                                        heading="Edit Tools"
                                        disabled={isSubmitting}
                                        enabledTools={enabledTools}
                                        onToggle={toggleTool}
                                        toolDefaults={toolDefaults}
                                        onToggleDefault={toggleDefaultTool}
                                    />
                                </div>

                                {/* Edit Users */}
                                <div className={`mt-6 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <UsersField
                                        heading="Edit Users"
                                        disabled={isSubmitting}
                                        selectedEntities={selectedEntities}
                                        onSelectEntity={handleEntitySelect}
                                        onRemoveEntity={handleEntityRemove}
                                    />
                                </div>

                                {/* Edit Managers */}
                                <div className={`mt-6 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <UsersField
                                        heading="Edit Managers"
                                        description="Managers have full control of the assistant: edit, files, enable/disable, delete, and manage this list."
                                        disabled={isSubmitting}
                                        selectedEntities={selectedManagerEntities}
                                        onSelectEntity={handleManagerSelect}
                                        onRemoveEntity={handleManagerRemove}
                                        lockedEntityIds={builderManagerGroupId ? [builderManagerGroupId] : []}
                                    />
                                </div>

                                {/* Edit Documents and Media */}
                                <div className={`mt-6 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <h4
                                        className={`text-lg font-semibold ${isSubmitting ? 'text-gray-500' : 'text-jtc-very-dark-grey'
                                            }`}
                                    >
                                        Edit Documents & Media
                                    </h4>
                                    <p
                                        className={`text-sm mb-4 ${isSubmitting ? 'text-gray-400' : 'text-gray-500'}`}
                                    >
                                        Add documents and media that your assistant will use to learn. These sources form
                                        the basis for your assistant's responses.
                                        <br />
                                        Manage your media library via the{" "}
                                        <a
                                            href="https://www.videoindexer.ai/media/library"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="underline"
                                        >
                                            Video Indexer portal.
                                        </a>
                                    </p>
                                    <div
                                        className={`mt-1 flex items-center justify-center px-6 h-48 border-2 border-dashed rounded-md ${isDragOver ? 'border-jtc-blue' : 'border-gray-400'
                                            }`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <div className="space-y-1 text-center">
                                            <AiOutlineFileAdd
                                                className={`mx-auto h-12 w-12 ${combinedFiles.length > 0 ? 'text-jtc-blue' : 'text-gray-400'
                                                    }`}
                                            />
                                            {combinedFiles.length > 0 ? (
                                                <>
                                                    <p className="text-sm text-gray-600">
                                                        {combinedFiles.length} file
                                                        {combinedFiles.length > 1 ? 's' : ''} selected
                                                        {documentCount > 0 && ` (${documentCount} document${documentCount > 1 ? 's' : ''})`}
                                                        {videoCount > 0 && ` (${videoCount} video${videoCount > 1 ? 's' : ''})`}
                                                    </p>
                                                    <div className="flex text-sm text-gray-600 justify-center">
                                                        <span>Drag and drop files here or</span>
                                                        <label
                                                            htmlFor="file-upload"
                                                            className="relative cursor-pointer bg-jtc-white rounded-md font-medium text-jtc-blue hover:text-jtc-blue focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-jtc-blue ml-1"
                                                        >
                                                            <span>Browse for more</span>
                                                            <input
                                                                id="file-upload"
                                                                name="file-upload"
                                                                type="file"
                                                                className="sr-only"
                                                                accept={KNOWLEDGE_ACCEPT}
                                                                multiple
                                                                ref={fileInputRef}
                                                                onChange={handleFileChange}
                                                            />
                                                        </label>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex text-sm text-gray-600 justify-center">
                                                    <label
                                                        htmlFor="file-upload"
                                                        className="relative cursor-pointer bg-jtc-white rounded-md font-medium text-jtc-blue hover:text-jtc-blue focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-jtc-blue"
                                                    >
                                                        <span>Browse for files</span>
                                                        <input
                                                            id="file-upload"
                                                            name="file-upload"
                                                            type="file"
                                                            className="sr-only"
                                                            accept={KNOWLEDGE_ACCEPT}
                                                            multiple
                                                            ref={fileInputRef}
                                                            onChange={handleFileChange}
                                                        />
                                                    </label>
                                                    <p className="pl-1">or drag and drop</p>
                                                </div>
                                            )}
                                            <p className="text-xs text-gray-500">
                                                Documents ({DOCUMENT_TYPES_LABEL}) up to {document_size_limit}MB, MP4 videos up to {video_size_limit_gb}GB
                                            </p>
                                        </div>
                                    </div>

                                    {/* Document & Video List and Counter */}
                                    <div
                                        className={`mt-4 space-y-2 ${isSubmitting ? 'opacity-50 pointer-events-none' : ''
                                            }`}
                                    >
                                        <div className="text-sm text-gray-500 text-right mb-1">
                                            {combinedFiles.length} / {file_limit}
                                        </div>
                                        {combinedFiles.map((uploadedFile, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between gap-2 text-sm text-gray-600"
                                            >
                                                <div className="flex items-center min-w-0">
                                                    <span
                                                        className="mr-2 flex-shrink-0"
                                                        style={{
                                                            width: '20px',
                                                            display: 'inline-block',
                                                            textAlign: 'right'
                                                        }}
                                                    >
                                                        {index + 1}.
                                                    </span>
                                                    {uploadedFile.type === 'document' ? (
                                                        <RiFileTextFill className="text-jtc-blue mr-2 flex-shrink-0" />
                                                    ) : (
                                                        <MdVideoLibrary className="text-jtc-purple mr-2 flex-shrink-0" />
                                                    )}
                                                    <span className="break-all">{uploadedFile.name}</span>
                                                    {uploadedFile.size && (
                                                        <span className="ml-2 text-gray-400">
                                                            ({formatFileSize(uploadedFile.size)})
                                                        </span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveCombinedFile(index)}
                                                    className="text-gray-500 hover:text-jtc-very-dark-grey ml-2"
                                                >
                                                    <MdClose />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="border-t border-jtc-disabled p-4 bg-white flex justify-end gap-4">
                        {!isSuccess && (
                            <button
                                disabled={isSubmitting}
                                className={`jtc-primary-button transition flex items-center justify-center ${isSubmitting
                                    ? "opacity-100 cursor-not-allowed"
                                    : "bg-jtc-pink hover:bg-jtc-purple"
                                    }`}
                                onClick={handleSubmit}
                            >
                                {isSubmitting ? <ClipLoader size={16} color="white" /> : "Save"}
                            </button>
                        )}
                        <button
                            className="jtc-secondary-button transition hover:bg-jtc-secondary-hover"
                            onClick={onCancel}
                        >
                            Back
                        </button>
                    </div>
                </div>
            </fieldset>
            <ErrorDialog
                open={showErrorDialog}
                setOpen={setShowErrorDialog}
                errorMessage={errorMessage}
            />
        </div>
    );
};
